import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LanguageProvider, useLanguage } from "@/contexts/LanguageContext";
import { ProtectedRoute } from "@/routes/ProtectedRoute";

// Register service worker (PWA)
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
    window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js").catch(() => {});
    });
}
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import LoginPage from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import UsersPage from "@/pages/Users";
import ProfilePage from "@/pages/Profile";
import ModulePlaceholder from "@/pages/ModulePlaceholder";
import ServicesPage from "@/pages/Services";
import ServiceOrderWizard from "@/pages/ServiceOrderWizard";
import ServiceOrderDetail from "@/pages/ServiceOrderDetail";
import CustomersPage from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import TicketsPage from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import FieldOpsPage from "@/pages/FieldOps";
import ConversationsPage from "@/pages/Conversations";
import KBPage from "@/pages/KnowledgeBase";
import SalesOverview from "@/pages/SalesOverview";
import SalesCopilot from "@/pages/SalesCopilot";
import ProposalsPage from "@/pages/Proposals";
import AnalyticsPage from "@/pages/Analytics";
import RevenuePage from "@/pages/Revenue";
import IntegrationsPage from "@/pages/Integrations";
import AdminLayout from "@/pages/admin/AdminLayout";
import AuditLogsPage from "@/pages/admin/AuditLogs";
import RBACMatrixPage from "@/pages/admin/RBACMatrix";
import SystemSettingsPage from "@/pages/admin/SystemSettings";
import TenantsPage from "@/pages/admin/Tenants";
import pt from "@/lib/i18n";

const Placeholder = ({ titleKey }) => (
    <ModulePlaceholder title={pt.nav[titleKey]} />
);

// Re-keys the entire route tree when the user toggles language, so any
// component that does `import pt from "@/lib/i18n"` re-reads via the Proxy.
const LanguageKeyedRoutes = ({ children }) => {
    const { lang } = useLanguage();
    return <React.Fragment key={lang}>{children}</React.Fragment>;
};

const App = () => (
    <AuthProvider>
        <ThemeProvider>
        <LanguageProvider>
        <BrowserRouter>
            <LanguageKeyedRoutes>
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route
                    element={
                        <ProtectedRoute>
                            <DashboardLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/perfil" element={<ProfilePage />} />
                    {/* Admin section — tabbed */}
                    <Route
                        path="/admin"
                        element={<ProtectedRoute moduleKey="admin"><AdminLayout /></ProtectedRoute>}
                    >
                        <Route index element={<Navigate to="utilizadores" replace />} />
                        <Route path="utilizadores" element={<UsersPage />} />
                        <Route path="auditoria" element={<AuditLogsPage />} />
                        <Route path="rbac" element={<RBACMatrixPage />} />
                        <Route path="sistema" element={<SystemSettingsPage />} />
                        <Route path="organizacao" element={<TenantsPage />} />
                    </Route>
                    {/* Service Ops */}
                    <Route
                        path="/servicos"
                        element={<ProtectedRoute moduleKey="services"><ServicesPage /></ProtectedRoute>}
                    />
                    <Route
                        path="/servicos/nova"
                        element={<ProtectedRoute moduleKey="services"><ServiceOrderWizard /></ProtectedRoute>}
                    />
                    <Route
                        path="/servicos/:orderId"
                        element={<ProtectedRoute moduleKey="services"><ServiceOrderDetail /></ProtectedRoute>}
                    />
                    {/* Customers */}
                    <Route
                        path="/clientes"
                        element={<ProtectedRoute moduleKey="customers"><CustomersPage /></ProtectedRoute>}
                    />
                    <Route
                        path="/clientes/:customerId"
                        element={<ProtectedRoute moduleKey="customers"><CustomerDetail /></ProtectedRoute>}
                    />
                    {/* Tickets */}
                    <Route
                        path="/tickets"
                        element={<ProtectedRoute moduleKey="tickets"><TicketsPage /></ProtectedRoute>}
                    />
                    <Route
                        path="/tickets/:ticketId"
                        element={<ProtectedRoute moduleKey="tickets"><TicketDetail /></ProtectedRoute>}
                    />
                    {/* Placeholders */}
                    <Route
                        path="/operacoes-campo"
                        element={<ProtectedRoute moduleKey="fieldOps"><FieldOpsPage /></ProtectedRoute>}
                    />
                    {/* CX Conversations + KB */}
                    <Route
                        path="/conversas"
                        element={<ProtectedRoute moduleKey="conversations"><ConversationsPage /></ProtectedRoute>}
                    />
                    <Route
                        path="/base-conhecimento"
                        element={<ProtectedRoute moduleKey="kb"><KBPage /></ProtectedRoute>}
                    />
                    <Route
                        path="/vendas"
                        element={<ProtectedRoute moduleKey="sales"><SalesOverview /></ProtectedRoute>}
                    />
                    <Route
                        path="/vendas/copiloto"
                        element={<ProtectedRoute moduleKey="sales"><SalesCopilot /></ProtectedRoute>}
                    />
                    <Route
                        path="/vendas/propostas"
                        element={<ProtectedRoute moduleKey="sales"><ProposalsPage /></ProtectedRoute>}
                    />
                    <Route
                        path="/receita"
                        element={<ProtectedRoute moduleKey="revenue"><RevenuePage /></ProtectedRoute>}
                    />
                    <Route
                        path="/analitica"
                        element={<ProtectedRoute moduleKey="analytics"><AnalyticsPage /></ProtectedRoute>}
                    />
                    <Route
                        path="/integracoes"
                        element={<ProtectedRoute moduleKey="integrations"><IntegrationsPage /></ProtectedRoute>}
                    />
                </Route>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
            </LanguageKeyedRoutes>
        </BrowserRouter>
        </LanguageProvider>
        </ThemeProvider>
    </AuthProvider>
);

export default App;
